import appManager from '@app/appManager';
import IAppLogger from '@app/lib/IAppLogger';
import AuthModule from '@modules/AuthModule';
import ToastModule from '@modules/ToastModule';
import * as RTCS from '@servicetitan/telecom-rtcs-client';
import { RtcsClient, RtcsOptions } from '@servicetitan/telecom-rtcs-client';
import getQueues from './api/getQueues';
import getUsers from './api/users/getUsers';
import handleAgentAway from './events/agent/handleAgentAway';
import handleAgentReady from './events/agent/handleAgentReady';
import handleAgentRecoveryCompleted from './events/agent/handleAgentRecoveryCompleted';
import handleAgentRecoveryStarted from './events/agent/handleAgentRecoveryStarted';
import handleAgentSessionDelivered from './events/agent/handleAgentSessionDelivered';
import handleAgentSessionOffered from './events/agent/handleAgentSessionOffered';
import handleAgentSessionRejected from './events/agent/handleAgentSessionRejected';
import handleAgentSessionRescinded from './events/agent/handleAgentSessionRescinded';
import handleAgentSessionTerminated from './events/agent/handleAgentSessionTerminated';
import handleAgentStatus from './events/agent/handleAgentStatus';
import handleQueueAgentAdded from './events/queue/handleQueueAgentAdded';
import handleQueueAgentRemoved from './events/queue/handleQueueAgentRemoved';
import handleQueueCreated from './events/queue/handleQueueCreated';
import handleQueueDeleted from './events/queue/handleQueueDeleted';
import handleQueueSessionDelivered from './events/queue/handleQueueSessionDelivered';
import handleQueueSessionJoin from './events/queue/handleQueueSessionJoin';
import handleQueueSessionLeft from './events/queue/handleQueueSessionLeft';
import handleQueueStatus from './events/queue/handleQueueStatus';
import handleSessionCreated from './events/session/handleSessionCreated';
import handleSessionDeleted from './events/session/handleSessionDeleted';
import log, { logException, setLogger } from './lib/log';
import { getUser } from './lib/requireUser';
import { ServiceState, serviceState } from './lib/state';
import { ServiceUserUtils } from './lib';
import { handleUserWrapUpStatusOnLoad } from './lib/wrapupStatus';
import { ContactCenterTelecomConfig } from '@modules/AuthModule/types';
import {
  trackTimeSyncFromRtcsEvent,
  logExistingTimeDiff,
} from './lib/clientServerTimeDiff';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
} from '@modules/DiagnosticsModule';
import {
  RTCSMetricNames,
  RTCSMetricTags,
  RTCSOperationNames,
  ConnectionStatus,
  RTCSStatus,
  WebPhoneStatus,
} from '../metrics/rtcsMetrics';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { categorizeServerError } from './lib/categorizeServerError';
import * as Sentry from '@sentry/react';
import { startConnectionStateMonitoring } from './lib/connectionStateMonitor';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { EventQueue } from './lib/EventQueue';

let workspaceClient: RtcsClient | null = null;

// EventQueue manages all workspace events to ensure strict sequential
// processing when enabled
let eventQueue: EventQueue | null = null;

function initializeEventQueue(): void {
  const isSequentialProcessingEnabled = FeatureFlagModule.isEnabled(
    FeatureFlagKeys.featureSequentialEventProcessing,
  );
  eventQueue = EventQueue.create(
    'workspace-events',
    isSequentialProcessingEnabled,
    isSequentialProcessingEnabled,
  );
}

async function resetEventQueue(): Promise<void> {
  if (eventQueue) {
    await eventQueue.reset();
  }
  eventQueue = null;
}

export function getWorkspaceClient(): RtcsClient | null {
  return workspaceClient;
}

export function getEventQueue(): EventQueue | null {
  return eventQueue;
}

export async function openWorkspace(
  telecomConfig: ContactCenterTelecomConfig,
  userId: string,
  accountId: string,
  tenantIds: string[],
  logger: IAppLogger,
) {
  setLoadingState(true, 'Connecting...');
  serviceState.error = null;

  // make sure we're not already connected
  await closeWorkspace();

  // Initialize event queue based on feature flag
  initializeEventQueue();

  // Log existing time diff if it was restored from storage
  logExistingTimeDiff();

  // save workspace globals
  setLogger(logger);
  serviceState.tenantIds = tenantIds;

  const rtcsHubUrl = telecomConfig.rtcsBaseAddress + '/v1/api/hub';
  log('workspace:open', rtcsHubUrl, userId, tenantIds);

  // save user id
  serviceState.id = userId;

  // Emit RTCS connection attempt metric BEFORE any operations that can fail
  DiagnosticsModule.incrementCounter(RTCSMetricNames.RTCS_CONNECTION_TOTAL, {
    [RTCSMetricTags.STATUS]: ConnectionStatus.ATTEMPTED,
  });

  // load our initial payloads
  try {
    const result = await Promise.all([getQueues(true), getUsers(userId, true)]);
    serviceState.queues = result[0];
    serviceState.users = result[1];
  } catch (err) {
    console.error('open workspace error', err);
    ToastModule.showToast({
      title: 'Failed to connect to the service',
      status: 'critical',
      primaryActionName: 'Retry',
      onPrimaryActionClick: () =>
        openWorkspace(telecomConfig, userId, accountId, tenantIds, logger),
    });
    serviceState.error = err;
    setLoadingState(false);
    return;
  }

  // get contact center id
  serviceState.contactCenterId = getUser(userId)?.contactCenterUserId ?? null;

  log('workspace:users-queues', serviceState.users, serviceState.queues);

  try {
    const authToken = await AuthModule.getCurrentToken();
    const options: RtcsOptions = {
      url: rtcsHubUrl,
      authToken,
      accountId,
    };

    // moved this after the token fetch to ensure we capture the latency of that operation in our RTCS connection metrics, since a token fetch is often required before connecting and can sometimes be slow due to network or auth provider issues
    const startTime = performance.now();
    workspaceClient = await RTCS.connect(options);
    await setupMessageListeners();

    // Start monitoring connection state
    startConnectionStateMonitoring(workspaceClient);

    // Record success metrics
    const latencyMs = performance.now() - startTime;
    DiagnosticsModule.incrementCounter(RTCSMetricNames.RTCS_CONNECTION_TOTAL, {
      [RTCSMetricTags.STATUS]: ConnectionStatus.SUCCESS,
    });
    DiagnosticsModule.recordHistogram(MetricNames.LATENCY_DURATION, latencyMs, {
      [MetricTags.OPERATION_NAME]: RTCSOperationNames.RTCS_LOGIN,
    });

    // check our user status
    const user = getUser(userId);
    if (!user) {
      throw new Error('Your user account is not properly initialized');
    } else {
      serviceState.contactCenterUserId = user.contactCenterUserId ?? null;
      if (ServiceUserUtils.isWrapUp(user)) {
        await handleUserWrapUpStatusOnLoad({ user });
      }
      appManager.events.emit('user.status.initialized', { user });
    }

    log('workspace:setup-complete');
    serviceState.serviceConnected = true;
  } catch (err) {
    // Record failure metrics (DiagnosticsModule handles flag check and error handling)
    DiagnosticsModule.incrementCounter(RTCSMetricNames.RTCS_CONNECTION_TOTAL, {
      [RTCSMetricTags.STATUS]: ConnectionStatus.FAILED,
      [MetricTags.ERROR_TYPE]: categorizeServerError(err),
    });

    // WebPhone correlation: check if WebPhone is registered when RTCS fails
    const webphoneRegistered = SoftPhoneModule.isPhoneConnected();
    DiagnosticsModule.incrementCounter(
      RTCSMetricNames.CONNECTION_CORRELATION_TOTAL,
      {
        [RTCSMetricTags.RTCS_STATUS]: RTCSStatus.FAILED,
        [RTCSMetricTags.WEBPHONE_STATUS]: webphoneRegistered
          ? WebPhoneStatus.REGISTERED
          : WebPhoneStatus.UNREGISTERED,
      },
    );

    // Send error to Sentry for debugging
    Sentry.captureException(err, {
      tags: { module: 'ServiceModule', operation: 'rtcs_login' },
      contexts: {
        rtcs: { userId, tenantCount: tenantIds.length },
        webphone: { isRegistered: SoftPhoneModule.isPhoneConnected() },
      },
    });

    // Start connection state monitoring even on failure
    // This ensures the monitor is ready for reconnection attempts
    startConnectionStateMonitoring(workspaceClient);

    serviceState.error = err;
  } finally {
    setLoadingState(false);
  }
}

export async function closeWorkspace() {
  try {
    if (workspaceClient) {
      log('workspace:close');
      workspaceClient.close();
      workspaceClient = null;
      serviceState.contactCenterUserId = null;
      serviceState.tenantIds = [];
      serviceState.id = null;
      serviceState.users = [];
      serviceState.userById = {};
      serviceState.userByContactCenterUserId = {};
      serviceState.sessions = {};
      serviceState.queues = [];
      serviceState.queueById = {};
      serviceState.error = null;
      serviceState.serviceConnected = false;
      await resetEventQueue(); // Wait for event queue to finish processing
      log('workspace:closed');
    }
  } catch (err) {
    logException(err, 'workspace:close');
  } finally {
    setLogger(null);
  }
}

export function setLoadingState(loading: boolean, status = '') {
  serviceState.loading = loading;
  serviceState.loadingStatus = status;
}

async function setupMessageListeners() {
  if (workspaceClient && eventQueue) {
    // Unified handler: All 3 listeners (agent.*, session.*, queue.*) call the same
    // function to eliminate callback timing variations. Separate callbacks could execute
    // with different timing (sync vs microtask delays), causing events to reach EventQueue
    // out of order. Using a single function ensures events enter EventQueue in the order
    // RTCS delivers them to our listeners.
    const handleEvent = (
      e: RTCS.AgentEvent | RTCS.SessionEvent | RTCS.QueueEvent,
    ) => {
      eventQueue!.process(
        async () => {
          // Track time sync on first event with timestamp (only tracks once internally)
          trackTimeSyncFromRtcsEvent(e, e.type);

          // Agent events
          if (e.type === 'agent.status') {
            return handleAgentStatus(e);
          } else if (e.type === 'agent.away') {
            return handleAgentAway(e);
          } else if (e.type === 'agent.ready') {
            return handleAgentReady(e);
          } else if (e.type === 'agent.sessionOffered') {
            return handleAgentSessionOffered(e);
          } else if (e.type === 'agent.sessionRescinded') {
            return handleAgentSessionRescinded(e);
          } else if (e.type === 'agent.sessionRejected') {
            return handleAgentSessionRejected(e);
          } else if (e.type === 'agent.sessionDelivered') {
            return handleAgentSessionDelivered(e);
          } else if (e.type === 'agent.sessionTerminated') {
            return handleAgentSessionTerminated(e);
          } else if (e.type === 'agent.wrapupStarted') {
            return handleAgentRecoveryStarted(e);
          } else if (e.type === 'agent.wrapupCompleted') {
            return handleAgentRecoveryCompleted(e);
          }
          // Session events
          else if (e.type === 'session.created') {
            return handleSessionCreated(e);
          } else if (e.type === 'session.deleted') {
            return handleSessionDeleted(e);
          }
          // Queue events
          else if (e.type === 'queue.sessionJoined') {
            return handleQueueSessionJoin(e);
          } else if (e.type === 'queue.status') {
            return handleQueueStatus(e);
          } else if (e.type === 'queue.sessionLeft') {
            return handleQueueSessionLeft(e);
          } else if (e.type === 'queue.sessionDelivered') {
            return handleQueueSessionDelivered(e);
          } else if (e.type === 'queue.created') {
            return handleQueueCreated(e);
          } else if (e.type === 'queue.deleted') {
            return handleQueueDeleted(e);
          } else if (e.type === 'queue.agentAdded') {
            return handleQueueAgentAdded(e);
          } else if (e.type === 'queue.agentRemoved') {
            return handleQueueAgentRemoved(e);
          }
        },
        e,
        () => !workspaceClient,
      );
    };

    // Register all 3 listeners in parallel to eliminate registration race condition
    // This ensures all listeners become active at the same time, preventing
    // out-of-order processing due to events arriving during sequential registration
    await Promise.all([
      workspaceClient.events.on('agent.*', handleEvent),
      workspaceClient.events.on('session.*', handleEvent),
      workspaceClient.events.on('queue.*', handleEvent),
    ]);
  }
}

export function applyMockedState<K extends keyof ServiceState>(
  prop: K,
  value: ServiceState[K],
) {
  serviceState[prop] = value;
}
